import { useRef } from 'react';

type TParams<TRun> = {
  pendingRunId: string | null;
  setPendingRunId: (runId: string | null) => void;
  setResponse: (response: TRun | null) => void;
};

/**
 * The one polled run whose results we still want. Cancelling or submitting anew moves the watch,
 * and a poll response landing afterwards (React Query does not abort an in-flight fetch) is
 * dropped instead of resurrecting the state that was just cleared.
 */
export const useWatchedRun = <TRun extends { runId: string }>({
  pendingRunId,
  setPendingRunId,
  setResponse,
}: TParams<TRun>) => {
  // Through a ref so a delivery from a stale closure still checks the CURRENT watch.
  const watchedRunIdRef = useRef<string | null>(null);
  watchedRunIdRef.current = pendingRunId;

  const deliver = (response: TRun | null) => {
    if (!response || response.runId !== watchedRunIdRef.current) return;
    setPendingRunId(null);
    setResponse(response);
  };

  return {
    watchedRunIdRef,
    deliver,
    watch: (runId: string) => setPendingRunId(runId),
    unwatch: () => setPendingRunId(null),
  };
};
